import { Injectable, inject } from '@angular/core';

import { OfferSource } from '@cds';
import {
    DetailedGrouping,
    DisplayType,
    Fixture,
    Game,
    Option,
    OptionMarket,
    OptionMarketStatus,
    OptionStatus,
    OrderType,
    Parameter,
    ParameterType,
    Participant,
    Translation,
} from '@cds/betting-offer';
import { BettingInsightsModel } from '@cds/betting-offer/betting-insights';
import { SplitFixture } from '@cds/betting-offer/domain-specific';
import { BetBuilderFixture } from '@cds/betting-offer/domain-specific/bet-builder';
import { FixtureOffer } from '@cds/generic-interface/response-items/unpacked';
import { ScoreboardSlim } from '@cds/scoreboards/v1';
import { PlayerStats } from '@cds/statistics/player';
import {
    EnhancedFixtureViewGroupingConfiguration,
    EventDetailsTags,
    EventModel,
    EventOptionGroup,
    ExtendedDisplayType,
    PRECREATED_BAB_EMPTY_THEME_ID,
    Tv2MarketParameters,
    getSourceEventId,
} from '@frontend/sports/betting-offer/feature/model';
import { BetBuilderConfig, EventDetailsConfig, PriceBoostConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { NativePrice } from '@frontend/sports/odds/feature/native-price';
import { NativeAppService } from '@frontend/vanilla/core';
import { cloneDeep } from 'lodash-es';

import { DetailedGroupingFactory } from '../detailed-grouping.factory';
import { CommonFixtureFactory } from './common-fixture.factory';
import { DetailedFixtureMarket, DetailedFixtureMarketFactory } from './detailed-fixture-market.factory';
import { EventSitemapGroupingFactory } from './event-sitemap-grouping.factory';
import { LinkedFixtureService } from './linked-fixture.service';

export interface EventDetailsParameters {
    fixture: Fixture;
    splitFixtures: SplitFixture[];
    grouping: EnhancedFixtureViewGroupingConfiguration;
    precreated?: BetBuilderFixture;
    isPriceBoosted?: boolean;
    loadEventSitemap?: boolean;
    bettingInsights?: BettingInsightsModel;
    excludePriceboostedMarketGrouping?: boolean;
    fixtureOffers?: FixtureOffer[];
}

export interface MarketGroupDetails {
    groupId: string;
    groupItemId: string | undefined;
}

@Injectable({ providedIn: 'root' })
export class DetailedCreateFactory {
    private fixtureFactory = inject(CommonFixtureFactory);
    private marketFactory = inject(DetailedFixtureMarketFactory);
    private groupingFactory = inject(DetailedGroupingFactory);
    private eventSitemapGroupingFactory = inject(EventSitemapGroupingFactory);
    private eventDetailsConfig = inject(EventDetailsConfig);
    sitecore = inject(Sitecore);
    betBuilderConfig = inject(BetBuilderConfig);
    linkedFixtureService = inject(LinkedFixtureService);
    private nativeAppService = inject(NativeAppService);
    private priceBoostConfig = inject(PriceBoostConfig);

    create(eventParameters: EventDetailsParameters): EventModel {
        const event = this.fixtureFactory.create(eventParameters.fixture);
        const optionGroups = this.createOptionGroups(
            eventParameters.fixture,
            eventParameters.splitFixtures,
            eventParameters.grouping,
            eventParameters.precreated,
            eventParameters.isPriceBoosted,
            eventParameters.excludePriceboostedMarketGrouping,
            eventParameters.fixtureOffers,
        );
        const betBuilderId = event.getBetBuilderIdByStage(
            this.betBuilderConfig.liveBetBuilderEnabledCompetitions || [],
            this.betBuilderConfig.liveBetBuilderEnabledSports || [],
        );

        const optionSets = this.groupingFactory.getOptionSets(
            optionGroups,
            eventParameters.grouping,
            eventParameters.fixture.source,
            betBuilderId,
            event.live,
        );

        this.setParticipantsShirtColor(event, eventParameters.fixture);

        event.marketsDictionary = this.createMarketsDictionary(optionGroups);
        event.splitFixtureIds = (eventParameters.splitFixtures && eventParameters.splitFixtures.map((splitFixture) => splitFixture.id)) || [];
        event.fixtureScoreboard = eventParameters.fixture.scoreboard as ScoreboardSlim;
        event.optionGroups = optionGroups;
        event.optionSets = optionSets;
        event.gamesCount = optionGroups.length;
        event.isPriceBoosted = eventParameters.isPriceBoosted;
        event.bettingInsights = eventParameters.bettingInsights;
        event.playerStats = this.getPlayerStats(eventParameters.fixture, eventParameters.splitFixtures);

        if (eventParameters.fixture.tv2LinkedFixture) {
            event.winGameLinkedFixture = eventParameters.fixture.tv2LinkedFixture;
            event.optionGroups = this.linkedFixtureService.updateLinkedGroups(event);
        }

        if (!!eventParameters.loadEventSitemap && this.eventDetailsConfig.enableSitemapNavigation) {
            event.sitemapSets = this.eventSitemapGroupingFactory.getSitemapSet(optionSets, eventParameters.grouping, optionGroups);
        }

        if (eventParameters.precreated?.precreatedGroupedInfo) {
            event.precreatedOptionGroups = eventParameters.precreated?.precreatedGroupedInfo;
        }

        return event;
    }

    //TODO please refactor this function if you are going to modify it, it's very complicated and must be refactored
    // eslint-disable-next-line max-lines-per-function
    createOptionGroups(
        fixture: Fixture,
        splitFixtures: SplitFixture[],
        grouping: EnhancedFixtureViewGroupingConfiguration,
        precreated?: BetBuilderFixture,
        isPriceBoosted?: boolean,
        excludePriceboostedMarketGrouping?: boolean,
        fixtureOffers: FixtureOffer[] = [],
        forceRegularDisplayType = true,
    ): EventOptionGroup[] {
        const result: EventOptionGroup[] = [];
        const mapper = (id: string, source?: DetailedFixtureMarket[], participants?: Participant[], isPartOfSgpGroup?: boolean) => {
            if (!source) {
                return [];
            }

            return (
                source?.flatMap((optionGroup) => {
                    const longIds = precreated?.precreatedMarketsInfo.find((marketInfo) => marketInfo.id === optionGroup.id)?.selections;

                    return this.marketFactory.create(
                        optionGroup,
                        id,
                        fixture.isOpenForBetting,
                        grouping,
                        participants,
                        isPriceBoosted,
                        longIds,
                        isPartOfSgpGroup,
                        excludePriceboostedMarketGrouping,
                        forceRegularDisplayType,
                    );
                }) ?? []
            );
        };

        result.push(...mapper(fixture.id, fixture.games, fixture.participants));
        result.push(...mapper(fixture.id, fixture.optionMarkets, fixture.participants));

        if ((this.priceBoostConfig.isEnabled || this.priceBoostConfig.isEnabledAPB) && grouping.apbGroups && fixture.priceBoostCount > 0) {
            const priceBoostMarketGroup = this.priceBoostConfig.showAsTab
                ? grouping.apbGroups.filter((p) => p.tag?.key === EventDetailsTags.PriceBoost)
                : grouping.apbGroups.filter(
                      (group) =>
                          group.tag?.key !== EventDetailsTags.PriceBoost &&
                          Object.values(group.marketGroupItems).some((item) => item?.tags.some((tag) => tag.key === EventDetailsTags.PriceBoost)),
                  );

            if (priceBoostMarketGroup.length > 0) {
                const filteredData = priceBoostMarketGroup.map((data) => {
                    return {
                        groupId: data.id,
                        groupItemId: Object.values(data.marketGroupItems).find((marketGroupItem) =>
                            marketGroupItem?.tags.some((tag) => tag.key === EventDetailsTags.PriceBoost),
                        )?.id,
                    };
                });

                if (filteredData.length > 0) {
                    const automatedPriceBoostMarkets = this.getAutomatedPriceBoostedMarkets(filteredData, fixture.optionMarkets);
                    result.push(...mapper(fixture.id, automatedPriceBoostMarkets));
                }
            }
        }

        if (
            this.betBuilderConfig.isBetBuilderEnabledForSport?.includes(fixture.sport.id) &&
            (this.nativeAppService.isTerminal || this.betBuilderConfig.themedTabs[fixture.sport.id])
        ) {
            const markets =
                precreated?.precreatedMarketsInfo.length && grouping.preCreatedGroups
                    ? this.getBetBuilderMarketsToBeAppended(fixture, grouping, precreated)
                    : fixture.optionMarkets;

            if (precreated?.sourceFixture?.id) {
                const precreatedFixtureId = getSourceEventId(precreated?.sourceFixture.id);
                result.push(...mapper(precreatedFixtureId, markets));
            }

            if (precreated?.precreatedGroupedInfo && grouping.preCreatedGroups) {
                const precreatedOptionGroups = this.getBetBuilderOptionGroupMarketsToBeAppended(fixture, grouping, precreated);

                result.push(...mapper(fixture.id, markets, undefined));
                result.push(...mapper(fixture.id, precreatedOptionGroups, undefined, true));
            }
        }

        splitFixtures.forEach((split) => {
            result.push(...mapper(split.id, split.games, fixture.participants));
        });

        fixtureOffers.forEach((fixtureOffer) => {
            result.push(...mapper(fixtureOffer.fixtureId, fixtureOffer.games, fixture.participants));
            result.push(...mapper(fixtureOffer.fixtureId, fixtureOffer.optionMarkets, fixture.participants));
        });

        return result;
    }

    private getAutomatedPriceBoostedMarkets(marketGroupItemsCollection: MarketGroupDetails[], optionMarkets: OptionMarket[]): OptionMarket[] {
        const boostedOptionMarkets: OptionMarket[] = [];

        marketGroupItemsCollection.map((data) => {
            if (data.groupItemId) {
                const parameters: Parameter[] = [
                    {
                        key: Tv2MarketParameters.MarketType,
                        type: ParameterType.String,
                        value: ExtendedDisplayType.AutomatedPriceBoost,
                    },
                ];
                const detailsGroups: DetailedGrouping = {
                    displayType: DisplayType.Regular,
                    orderType: OrderType.None,
                    marketHelpPath: '',
                    marketGroupId: data.groupId,
                    marketGroupItemId: data.groupItemId,
                    contextInfo: '',
                };
                optionMarkets.reduce((res: OptionMarket[], optionMarket) => {
                    const boostedOptions = optionMarket.options.filter(
                        (option) => option.boostedPrice && NativePrice.fromNativePrice(option.boostedPrice).greaterThan(1),
                    );

                    if (boostedOptions.length) {
                        const boostedOptionMarket = cloneDeep(optionMarket);
                        boostedOptionMarket.options = cloneDeep(boostedOptions);
                        boostedOptionMarket.grouping = {
                            detailed: [detailsGroups],
                            gridGroups: [],
                        };
                        boostedOptionMarket.parameters = parameters;
                        res.push(boostedOptionMarket);
                    }

                    return res;
                }, boostedOptionMarkets);
            }
        });

        return boostedOptionMarkets;
    }

    private getPlayerStats(fixture: Fixture, splitFixtures: SplitFixture[]): PlayerStats[] {
        if (fixture.playerStats?.some((x) => x.source === OfferSource.V1)) {
            return fixture.playerStats;
        }

        const playerStats = fixture.playerStats ?? [];
        const splitWithStats = splitFixtures.find((split) => !!split.playerStats);

        if (splitWithStats) {
            playerStats.push(splitWithStats.playerStats);
        }

        return playerStats;
    }

    //TODO please refactor below function if you are going to modify anything within it.
    // eslint-disable-next-line max-lines-per-function
    private getBetBuilderMarketsToBeAppended(
        fixture: Fixture,
        grouping: EnhancedFixtureViewGroupingConfiguration,
        precreated?: BetBuilderFixture,
    ): OptionMarket[] {
        const precreatedModel = cloneDeep(precreated);
        if (precreatedModel?.precreatedMarketsInfo.length) {
            let optionMarkets: OptionMarket[] | undefined;
            optionMarkets = precreatedModel?.sourceFixture
                ? precreatedModel?.sourceFixture.optionMarkets.filter((market) => {
                      return precreatedModel.precreatedMarketsInfo.some((f) => {
                          return f.id === market.id;
                      });
                  })
                : [];
            const emptyMarketTabs = grouping.marketTabs.filter((x) => x.name === '');
            optionMarkets?.forEach((precreatedBAB) => {
                const themedName = precreatedModel?.precreatedMarketsInfo.find((x) => x.id === precreatedBAB.id);
                let marketTabId;
                if (!this.nativeAppService.isTerminal) {
                    marketTabId = grouping.marketTabs.find((x) => x.name?.toUpperCase() === themedName?.themedTab?.toUpperCase());
                }
                if (marketTabId === undefined || marketTabId.name === '') {
                    marketTabId = emptyMarketTabs[0];
                    marketTabId.id = PRECREATED_BAB_EMPTY_THEME_ID;
                }
                const parameters: Parameter[] = [];
                const parameter: Parameter = {
                    key: Tv2MarketParameters.MarketType,
                    type: ParameterType.String,
                    value: ExtendedDisplayType.BetBuilder,
                };
                parameters.push(parameter);
                const detailedGrouping: DetailedGrouping[] = [];
                const preCreatedMarketGroups = !this.betBuilderConfig.enablePrecreatedBetBuilderTab
                    ? grouping.preCreatedGroups.filter((g) => g.tag?.key !== EventDetailsTags.PreCreatedBab)
                    : grouping.preCreatedGroups;

                for (const group of preCreatedMarketGroups) {
                    for (const marketGroupItemId of Object.keys(group.marketGroupItems)) {
                        detailedGrouping.push({
                            displayType: DisplayType.Regular,
                            orderType: OrderType.None,
                            marketTabId: marketTabId?.id,
                            marketHelpPath: this.sitecore.betBuilder.messages?.PrecreatedBuildABetTitleHelpText ?? '',
                            marketGroupId: group.id,
                            contextInfo: '',
                            marketGroupItemId: marketGroupItemId,
                        });
                    }
                }
                // eslint-disable-next-line no-param-reassign
                precreatedBAB.grouping = {
                    detailed: detailedGrouping,
                    gridGroups: [],
                    // eslint-disable-next-line max-lines
                };
                const market = precreatedModel.precreatedMarketsInfo.find((x) => x.id === precreatedBAB.id);

                // eslint-disable-next-line no-param-reassign, @typescript-eslint/no-non-null-assertion
                precreatedBAB.name = { sign: market!.themedTab, value: market!.themedTab };
                // eslint-disable-next-line no-param-reassign, @typescript-eslint/no-non-null-assertion
                precreatedBAB.id = market!.id;
                // eslint-disable-next-line no-param-reassign
                precreatedBAB.parameters = parameters;
                // eslint-disable-next-line no-param-reassign
                precreatedBAB.status = OptionMarketStatus.Visible;
                // eslint-disable-next-line no-param-reassign
                precreatedBAB.options = this.mapOptions(precreatedBAB, precreated);
                fixture.optionMarkets.push(precreatedBAB);
            });
        }

        return fixture.optionMarkets;
    }

    //TODO please refactor below function
    // eslint-disable-next-line max-lines-per-function
    private getBetBuilderOptionGroupMarketsToBeAppended(
        fixture: Fixture,
        grouping: EnhancedFixtureViewGroupingConfiguration,
        precreated?: BetBuilderFixture,
    ): DetailedFixtureMarket[] {
        const precreatedModel = cloneDeep(precreated);
        const emptyMarketTab = grouping.marketTabs.find((x) => x.name === '');
        const markets: DetailedFixtureMarket[] = [];

        if (precreatedModel?.precreatedGroupedInfo.length) {
            const preCreatedMarketGroups = !this.betBuilderConfig.enablePrecreatedBetBuilderTab
                ? grouping.preCreatedGroups.filter((g) => g.tag?.key !== EventDetailsTags.PreCreatedBab)
                : grouping.preCreatedGroups;
            // eslint-disable-next-line max-lines-per-function
            precreatedModel?.precreatedGroupedInfo.forEach((group) =>
                // eslint-disable-next-line max-lines-per-function
                group.options.forEach((option) => {
                    const precreatedBAB = fixture.games.length
                        ? fixture.games.find((market) => market.id === option.marketId)
                        : fixture.optionMarkets.find((market) => market.id === option.marketId);
                    if (!precreatedBAB) {
                        return;
                    }
                    const precreatedBABModel = cloneDeep(precreatedBAB);
                    const detailedGrouping: DetailedGrouping[] = [];
                    for (const preCreatedGroup of preCreatedMarketGroups) {
                        for (const marketGroupItemId of Object.keys(preCreatedGroup.marketGroupItems)) {
                            detailedGrouping.push({
                                displayType: DisplayType.Regular,
                                orderType: OrderType.None,
                                marketTabId: emptyMarketTab?.id,
                                marketHelpPath: this.sitecore.betBuilder.messages?.PrecreatedBuildABetTitleHelpText ?? '',
                                marketGroupId: preCreatedGroup.id,
                                contextInfo: '',
                                marketGroupItemId: marketGroupItemId,
                            });
                        }
                    }
                    precreatedBABModel.grouping = {
                        detailed: detailedGrouping,

                        gridGroups: [],
                    };
                    // precreatedBABModel is TV1 Game
                    if ('visibility' in precreatedBABModel) {
                        const foundOption = precreatedBABModel.results.find((o) => o.id === option.id);

                        if (foundOption) {
                            const foundMarket = markets.find((market) => market.id === precreatedBABModel.id) as Game | undefined;
                            if (foundMarket) {
                                foundMarket.results.push(foundOption);
                            } else {
                                precreatedBABModel.results = [foundOption];
                                markets.push(precreatedBABModel);
                            }
                        }
                    }
                    // precreatedBABModel is TV2 OptionMarket
                    if ('status' in precreatedBABModel) {
                        precreatedBABModel.parameters = [
                            {
                                key: Tv2MarketParameters.MarketType,
                                type: ParameterType.String,
                                value: ExtendedDisplayType.BetBuilder,
                            },
                        ];
                        const foundOption = precreatedBABModel.options.find((o) => o.id === option.id);

                        if (foundOption) {
                            const foundMarket = markets.find((market) => market.id === precreatedBABModel.id) as OptionMarket | undefined;
                            if (foundMarket) {
                                foundMarket.options.push(foundOption);
                            } else {
                                precreatedBABModel.options = [foundOption];
                                markets.push(precreatedBABModel);
                            }
                        }
                    }
                }),
            );
        }

        return markets;
    }

    private mapOptions(precreatedBAB: OptionMarket, precreated: BetBuilderFixture | undefined): Option[] {
        return precreatedBAB.options.map((option) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const currentMarketName = precreated?.sourceFixture.optionMarkets.find((x) => x.id === precreatedBAB.id)!.name;
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const optionMarketName: Translation = { sign: currentMarketName!.sign, value: currentMarketName!.value };

            return {
                ...option,
                price: option.price,
                name: optionMarketName,
                status: OptionStatus.Visible,
                sourceName: option.sourceName,
                id: option.id,
            };
        });
    }

    private createMarketsDictionary(optionGroups: EventOptionGroup[]): Record<string, EventOptionGroup[]> {
        return optionGroups.reduce<Record<string, EventOptionGroup[]>>((marketsDictionary, group) => {
            const ids = [group.id, ...(group.groupedMarkets || [])];

            ids.forEach((id) => {
                if (!marketsDictionary[id]) {
                    // eslint-disable-next-line no-param-reassign
                    marketsDictionary[id] = [];
                }

                marketsDictionary[id].push(group);
            });

            return marketsDictionary;
        }, {});
    }

    private setParticipantsShirtColor(eventModel: EventModel, fixture: Fixture): void {
        const animationSports = [SportConstant.Basketball, SportConstant.Tennis, SportConstant.Icehockey];
        const fallbackColors: string[] = this.eventDetailsConfig.teamIndicatorFallbackColors.slice(0, 2);
        //TODO any index issue, probably a defect, please consider adding player info to cds scoreboard type
        const hasPlayerInfo = fixture.scoreboard && fixture.scoreboard['playerInfo' as keyof typeof fixture.scoreboard];

        const playerInfo = hasPlayerInfo && (fixture.scoreboard['playerInfo' as keyof typeof fixture.scoreboard] as any);

        const isColorsAvailable = hasPlayerInfo && playerInfo.colorsAvailable;

        const areTeamColorsSame = isColorsAvailable && playerInfo.players[`01`].shirtColor === playerInfo.players[`02`].shirtColor;

        eventModel.participants.forEach((participant, index) => {
            // eslint-disable-next-line no-param-reassign
            participant.shirtColor =
                isColorsAvailable && !areTeamColorsSame
                    ? playerInfo.players[`0${index + 1}`].shirtColor
                    : animationSports.indexOf(eventModel.sport.id) > -1 || areTeamColorsSame
                      ? fallbackColors[index]
                      : null;
        });
    }
}
